import { useEffect } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useGetSeason } from '@wbk/api/hooks';
import { FAQGroups } from '@wbk/ticketing';
import { useQueue } from '@wbk/queue';
import SeasonPageBanner from '@/components/season/SeasonPageBanner';
import ReserveSpotBanner from '@/components/season/ReserveSpotBanner';
import MatchCard from '@/components/match/MatchCard';
import Fade from '@/components/animation/Fade';
import SeasonTicketPoints from '@/components/season/SeasonTicketPoints';
import FootballSpinner from '@/components/ui/spinners/FootballSpinner';
import DeflatedBallWrapper from '@/components/error/DeflatedBallWrapper';

const SeasonTicketPage = () => {
  const { t } = useTranslation();
  const { locale, query } = useRouter();
  const { initQueue } = useQueue();
  const isPrivate = query.private === 'true';

  const {
    data: season,
    isLoading,
    error,
  } = useGetSeason({
    slug: query.queuedSlug as string,
    lang: locale,
    private: isPrivate,
  });

  useEffect(() => {
    initQueue('season');
  }, [initQueue]);

  if (isLoading) {
    return (
      <div className='mx-auto my-20 max-w-lg space-y-4'>
        <FootballSpinner className='text-primary-dark mx-auto h-80 w-80' />
      </div>
    );
  }

  if (error || !season) {
    return (
      <DeflatedBallWrapper>
        <div className='mx-auto max-w-xl rounded-lg bg-secondary-400/80 px-6 py-10 shadow-md'>
          <p className='mb-2 text-center text-3xl text-error'>
            {error?.message || 'Something went wrong'}
          </p>
        </div>
      </DeflatedBallWrapper>
    );
  }

  const background_color = season.background_color;
  const action_color = season.action_color;
  const text_color = season.text_color;

  return (
    <>
      <NextSeo
        title={t('tickets:seo.season_title')}
        description={t('tickets:seo.season_description')}
        additionalMetaTags={[{ name: 'keywords', content: t('home:seo.season_keywords') }]}
      />
      <SeasonPageBanner season={season} />
      {season?.benefits.length > 0 && (
        <section
          className='pb-10 pt-12'
          style={{ backgroundColor: background_color, color: action_color }}
        >
          <SeasonTicketPoints season={season} />
        </section>
      )}
      {season.description && (
        <section className='py-20' style={{ color: text_color, backgroundColor: background_color }}>
          <div className='container relative grid grid-cols-1 gap-8 lg:grid-cols-3'>
            <div className='col-span-full lg:col-span-2'>
              <h2 className='pb-4 text-4xl font-bold'>{t('event:about_season')}</h2>
              <div
                className='prose inline'
                dangerouslySetInnerHTML={{ __html: season.description || '' }}
              />
            </div>
          </div>
        </section>
      )}
      {season.events.length > 0 && (
        <section className='relative z-10 bg-white py-12'>
          <div className='container'>
            <h2 className='pb-8 text-4xl font-bold text-secondary-600'>
              {t('tickets:matches_included', { total: season.matches_count })}
            </h2>
            <div className='grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'>
              {season.events.map((match, i) => (
                <Fade delay={0.05 * i} threshold={0} key={match._id}>
                  <MatchCard match={match} />
                </Fade>
              ))}
            </div>
          </div>
        </section>
      )}
      <ReserveSpotBanner season={season} />
      <FAQGroups organizationSlug={process.env.NEXT_PUBLIC_ORGANIZATION_SLUG} />
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<LayoutProps> = async ({ locale = '' }) => {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
        'auth_common',
        'tickets',
        'event',
        'auth',
        'profile',
        'queue_common',
        'ticketing_common',
        'ticketing_event',
      ])),
    },
  };
};

export default SeasonTicketPage;
